import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Tabs, Modal } from 'antd';
import { FaSave, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useLocationMaster } from '../../hooks/useLocationMaster';
import { useMasterFacilityTypes } from '../../hooks/useMasterFacilityTypes';
import { useMasterFacilitySubTypes } from '../../hooks/useMasterFacilitySubTypes';
import { useMasterFacilitySubSubTypes } from '../../hooks/useMasterFacilitySubSubTypes';
import { useMasterConditionFacility } from '../../hooks/useMasterConditionFacility';
import { useMasterOperationalStatus } from '../../hooks/useMasterOperationalStatus';
import { serializeInfrastructureListingForm } from '../../utils/infrastructureListingFormSerializer';
import { validateInfrastructureListingForPublish } from '../../utils/validateInfrastructureListingPublish';
import CommonService from '../../services/common.service';
import { saveListingDraftAPI, publishListingAPI, postMyPublishedListingInventory } from '../../services/infrastructureLeasingListing.service';
import { buildSellerInventoryPostBodyFromListingForm } from '../../utils/infrastructureListingInventoryAvailabilityUi';
import { INFRA_ASSET_SIZE_OPTIONS } from '../../constants/infrastructureLeasingAssetSizes';
import { DURATION_TYPE_OPTIONS as INFRA_LEASE_DURATION_TYPES } from '../../constants/infrastructureLeasingDurationTypes';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/styles/seller_profile.scss';
import '../../assets/styles/infrastructure_leasing_list_asset.scss';

let TabPane;
if (Tabs.TabPane) {
  TabPane = Tabs.TabPane;
} else {
  TabPane = ({ children, ...props }) => <div {...props}>{children}</div>;
}

/** Listing type cards — same icon as the design (outline building / complex, #EE6C25). */
const ListingTypeBuildingIcon = () => (
  <svg
    className="ile-listing-type-icon"
    viewBox="0 0 88 60"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <g
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* shared ground line */}
      <line x1="2" y1="56" x2="86" y2="56" />
      {/* left wing (shorter) */}
      <rect x="4" y="32" width="20" height="24" rx="2" />
      {/* central tower (tall) + three window lines */}
      <rect x="32" y="4" width="24" height="52" rx="2.5" />
      <line x1="36" y1="16" x2="52" y2="16" />
      <line x1="36" y1="25" x2="52" y2="25" />
      <line x1="36" y1="34" x2="52" y2="34" />
      {/* right wing (shorter) */}
      <rect x="64" y="32" width="20" height="24" rx="2" />
    </g>
  </svg>
);

const IleIcon = () => (
  <svg className="ile-ie-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="M2 20h20v-8H2v8Z" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <path d="M6 20v-4M10 20v-4M14 20v-4M18 20v-4" stroke="currentColor" strokeWidth="1.5" />
    <path d="M2 12h20V8l-10-4-10 4v4Z" stroke="currentColor" strokeWidth="1.5" fill="none" />
  </svg>
);

const tabIcon = (d) => (
  <span className="tab-icon" aria-hidden>
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {typeof d === 'string' ? <path d={d} /> : d}
    </svg>
  </span>
);

/** @param {File|{name?: string, accessPath?: string}|string|unknown} x */
const mediaEntryName = (x) => {
  if (x == null) {
    return '';
  }
  if (typeof x === 'string') {
    return x.split('/').filter(Boolean).pop() || x;
  }
  if (typeof x === 'object' && (x.name || x.accessPath)) {
    if (x.name) {
      return x.name;
    }
    if (x.accessPath) {
      return String(x.accessPath).split('/').filter(Boolean).pop() || 'Uploaded';
    }
  }
  return 'File';
};

const mediaEntryHref = (x) => {
  if (x == null) {
    return '';
  }
  if (typeof x === 'string' && x.trim()) {
    return x;
  }
  if (typeof x === 'object' && x.accessPath && String(x.accessPath).trim()) {
    return x.accessPath;
  }
  return '';
};

const defaultForm = () => ({
  listingType: 'property', // property | machinery
  listingTitle: '',
  assetCategory: '',
  subCategory: '',
  subSubCategory: '',
  legacyCategory: '',
  ownershipType: 'direct_owner',
  transactionType: 'lease',
  assetSize: 'any_size', // see INFRA_ASSET_SIZE_OPTIONS
  quantityAvailable: '', // only shown & used when assetSize is micro or small
  countryId: '',
  stateId: '',
  cityId: '',
  fullAddress: '',
  postalCode: '',
  industrialZone: '',
  propertyType: '',
  carpetArea: '',
  builtUpArea: '',
  landArea: '',
  zoning: '',
  furnishing: '',
  power: false,
  powerLoadKw: '',
  water: false,
  loading: false,
  parking: '',
  parkingType: '',
  roadAccessFt: '',
  cFire: false,
  cEnv: false,
  cTrade: false,
  cOcc: false,
  cPol: false,
  construction: '',
  occupancy: '',
  equipmentBrand: '',
  equipmentModel: '',
  equipmentMake: '',
  serialNumber: '',
  manufactureYear: '',
  equipmentCondition: '',
  operationalStatus: '',
  usageHours: '',
  maxCapacity: '',
  capacityUnit: '',
  dimLength: '',
  dimWidth: '',
  dimHeight: '',
  netWeight: '',
  weightUnit: '',
  installationLocation: '',
  relocationPossible: false,
  relocationCost: '',
  operatorIncluded: false,
  operatorAddOnCost: '',
  fuelType: '',
  fuelConsumption: '',
  insuranceStatus: '',
  insuranceCost: '',
  transportResponsibility: '',
  transportCost: '',
  pricingModel: 'monthly_long_lease',
  rentPerMonth: '',
  deposit: '',
  maintenance: '',
  durationType: 'days',
  availableDuration: '',
  minTenure: '',
  maxTenure: '',
  lockin: '',
  flexibleTenure: true,
  availableFrom: '',
  description: '',
  connectivity: '',
  contactVisibility: 'public', // public | hidden | broker
  images: [],
  videos: [],
  floorPlan: null,
  drone: null,
  complianceDocs: null,
  /** Phase 3: optional normalized inventory (POST after first publish). */
  inventoryTrackEnabled: false,
  inventoryKind: 'unit',
  inventoryLabel: '',
  inventoryTotalUnits: '',
  inventoryTotalArea: '',
  inventoryTotalCapacity: '',
  inventoryAreaUom: 'sqft',
  inventoryCapacityUom: '',
  inventoryPartialLeasingAllowed: true,
  inventoryMinLeasableUnits: '',
  inventoryMinLeasableArea: '',
  inventoryMinLeasableDuration: '',
  inventoryHybridNote: '',
});

/** Form value → display label (maps for submission / preview). */
const LEGACY_CATEGORY_OPTIONS = [
  { value: 'office_space', label: 'Office Space' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'factory', label: 'Factory' },
  { value: 'industrial_shed', label: 'Industrial Shed' },
  { value: 'retail_shop', label: 'Retail Shop' },
  { value: 'land', label: 'Land' },
  { value: 'cold_storage', label: 'Cold Storage' },
];

const OWNERSHIP_OPTIONS = [
  { value: 'direct_owner', label: 'Direct Owner' },
  { value: 'broker_agent', label: 'Broker/Agent' },
];

const TRANSACTION_TYPE_OPTIONS = [
  { value: 'lease', label: 'Lease' },
  { value: 'rent', label: 'Rent' },
  { value: 'sale', label: 'Sale' },
  { value: 'lease_to_own', label: 'Lease to Own' },
];

/** Property type * — stored values map to product labels. */
const PROPERTY_TYPE_OPTIONS = [
  { value: 'office_space', label: 'Office Space' },
  { value: 'retail_shop', label: 'Retail Shop' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'factory', label: 'Factory' },
  { value: 'industrial_shed', label: 'Industrial Shed' },
  { value: 'coworking_space', label: 'Coworking Space' },
  { value: 'land', label: 'Land' },
  { value: 'cold_storage', label: 'Cold Storage' },
  { value: 'agricultural_land', label: 'Agricultural Land/Unit' },
  { value: 'hospitality_property', label: 'Hospitality Property' },
];

const FURNISHING_STATUS_OPTIONS = [
  { value: 'bare_shell', label: 'Bare Shell' },
  { value: 'warm_shell', label: 'Warm Shell' },
  { value: 'fully_furnished', label: 'Fully Furnished' },
  { value: 'semi_furnished', label: 'Semi Furnished' },
];

const PARKING_TYPE_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'covered', label: 'Covered' },
  { value: 'basement', label: 'Basement' },
];

const CONSTRUCTION_STATUS_OPTIONS = [
  { value: 'ready_to_move', label: 'Ready to Move' },
  { value: 'under_construction', label: 'Under Construction' },
  { value: 'upcoming', label: 'Upcoming' },
];

const OCCUPANCY_STATUS_OPTIONS = [
  { value: 'vacant', label: 'Vacant' },
  { value: 'occupied', label: 'Occupied' },
  { value: 'partially_occupied', label: 'Partially Occupied' },
];

const PRICING_MODEL_OPTIONS = [
  { value: 'monthly_long_lease', label: 'Monthly Rate (Long-term lease)' },
  { value: 'daily_short_rental', label: 'Daily Rate (Short-term rental)' },
  { value: 'hourly_very_short', label: 'Hourly Rate (Very short-term)' },
];

/** Matches backend `infrastructure_listing_inventory.inventory_kind`. */
const INVENTORY_MODEL_OPTIONS = [
  { value: 'unit', label: 'Unit-based (e.g. bays, machines, rooms)' },
  { value: 'area', label: 'Area-based (continuous sq ft / land)' },
  { value: 'capacity', label: 'Capacity-based (weight, pallets, throughput)' },
  { value: 'time', label: 'Time-based (calendar area slot)' },
  { value: 'hybrid', label: 'Hybrid (multiple dimensions)' },
];

/** Machinery / asset tab (listing type: Machinery) */
const MACHINERY_WEIGHT_UNIT_OPTIONS = [
  { value: 'kg', label: 'Kilograms (kg)' },
  { value: 'tonnes', label: 'Metric tonnes' },
  { value: 'lbs', label: 'Pounds (lbs)' },
  { value: 'g', label: 'Grams (g)' },
];

const MACHINERY_INSURANCE_STATUS_OPTIONS = [
  { value: '', label: 'Select status' },
  { value: 'insured', label: 'Insured' },
  { value: 'not_insured', label: 'Not Insured' },
  { value: 'insurance_optional', label: 'Insurance Optional' },
];

const MACHINERY_TRANSPORT_BY_OPTIONS = [
  { value: '', label: 'Select' },
  { value: 'buyer', label: 'Buyer' },
  { value: 'seller', label: 'Seller' },
  { value: 'shared', label: 'Shared' },
  { value: 'negotiable', label: 'Negotiable' },
];

/** Same tiers as post-requirement & dropdown copy */
const PICK_ASSET_SIZES = INFRA_ASSET_SIZE_OPTIONS;

const LISTING_TYPE_OPTIONS = [
  {
    id: 'property',
    title: 'Property',
    sub: 'Office, Warehouse, Factory, Land.',
  },
  {
    id: 'machinery',
    title: 'Machinery / Asset',
    sub: 'Equipment, Machinery, Infrastructure.',
  },
];

export default function InfrastructureLeasingListAssetPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('1');
  const [f, setF] = useState(defaultForm);
  const [unsavedOpen, setUnsavedOpen] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [mediaUploading, setMediaUploading] = useState(false);

  const { countries, states, cities, loading, loadingStates, loadingCities, updateListsForCountry, updateListsForState } = useLocationMaster();
  const { list: facilityTypes, loading: loadingFacilityTypes } = useMasterFacilityTypes(f.listingType);
  const { list: facilitySubTypes, loading: loadingSubTypes } = useMasterFacilitySubTypes(f.assetCategory);
  const { list: facilitySubSubTypes, loading: loadingSubSubTypes } = useMasterFacilitySubSubTypes(
    f.listingType === 'machinery' ? f.subCategory : null
  );
  const { list: conditionFacilities, loading: loadingConditionFacility } = useMasterConditionFacility(
    f.listingType === 'machinery'
  );
  const { list: operationalStatuses, loading: loadingOperationalStatus } = useMasterOperationalStatus(
    f.listingType === 'machinery'
  );

  const countryOptions = useMemo(
    () => (countries || []).map((c) => ({ value: String(c.id), label: c.label })),
    [countries]
  );
  const stateOptions = useMemo(
    () => (states || []).map((s) => ({ value: String(s.id), label: s.label })),
    [states]
  );
  const cityOptions = useMemo(
    () => (cities || []).map((c) => ({ value: String(c.id), label: c.label })),
    [cities]
  );

  const stepNum = Math.min(6, Math.max(1, parseInt(activeTab, 10) || 1));
  const progressPct = Math.round((stepNum / 6) * 100);

  const up = (patch) => setF((prev) => ({ ...prev, ...patch }));
  const setField = (k) => (e) => up({ [k]: e.target ? e.target.value : e });
  const onAssetCategoryChange = (e) => {
    up({ assetCategory: e.target.value, subCategory: '', subSubCategory: '' });
  };
  const onSubCategoryChange = (e) => {
    up({ subCategory: e.target.value, subSubCategory: '' });
  };
  const setChecked = (k) => (e) => up({ [k]: e.target.checked });
  const onPowerToggle = (e) => {
    const checked = e.target.checked;
    up(checked ? { power: true } : { power: false, powerLoadKw: '' });
  };
  const onRelocationToggle = (e) => {
    const checked = e.target.checked;
    up(checked ? { relocationPossible: true } : { relocationPossible: false, relocationCost: '' });
  };
  const onOperatorToggle = (e) => {
    const checked = e.target.checked;
    up(checked ? { operatorIncluded: true } : { operatorIncluded: false, operatorAddOnCost: '' });
  };

  useEffect(() => {
    if (!f.countryId) {
      return;
    }
    updateListsForCountry(f.countryId);
  }, [f.countryId, updateListsForCountry]);

  useEffect(() => {
    if (!f.countryId || !f.stateId) {
      return;
    }
    updateListsForState(f.countryId, f.stateId);
  }, [f.stateId, f.countryId, updateListsForState]);

  const onCountry = (e) => {
    const v = e.target.value;
    up({ countryId: v, stateId: '', cityId: '' });
  };
  const onState = (e) => {
    up({ stateId: e.target.value, cityId: '' });
  };

  const subSubRequiredMissing = () => {
    if (f.listingType !== 'machinery') return false;
    if (!f.subCategory) return false;
    if (!facilitySubSubTypes.length) return false;
    return !String(f.subSubCategory || '').trim();
  };

  const goPrev = () => {
    if (stepNum <= 1) return;
    setActiveTab(String(stepNum - 1));
  };
  const goNext = () => {
    if (stepNum === 1 && subSubRequiredMissing()) {
      toast.error('Please select a sub-sub-category.');
      return;
    }
    if (stepNum >= 6) return;
    setActiveTab(String(stepNum + 1));
  };

  const getUploadCompanyName = useCallback(() => {
    const t = (f.listingTitle || '').trim();
    return t.length > 0 ? t : 'InfrastructureListing';
  }, [f.listingTitle]);

  const uploadBatches = useCallback(
    async (fileList, fileType) => {
      const companyName = getUploadCompanyName();
      const all = [];
      for (let i = 0; i < fileList.length; i += 10) {
        const batch = fileList.slice(i, i + 10);
        const formData = new FormData();
        batch.forEach((file) => {
          formData.append('files', file);
        });
        formData.append('category', 'company');
        formData.append('companyName', companyName);
        formData.append('fileType', fileType);
        const res = await CommonService.post('/upload/multiple', formData);
        const fileResults = res?.data?.files;
        if (!res?.success || !Array.isArray(fileResults) || fileResults.length !== batch.length) {
          throw new Error(res?.message || 'Upload failed');
        }
        batch.forEach((file, j) => {
          const ap = fileResults[j]?.accessPath;
          if (!ap) {
            throw new Error('No file path in upload response');
          }
          all.push({ accessPath: ap, name: file.name });
        });
      }
      return all;
    },
    [getUploadCompanyName]
  );

  const uploadOneToCompany = useCallback(
    async (file, fileType) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'company');
      formData.append('companyName', getUploadCompanyName());
      formData.append('fileType', fileType);
      const res = await CommonService.post('/upload/single', formData);
      if (!res?.success || !res?.data?.accessPath) {
        throw new Error(res?.message || 'Upload failed');
      }
      return { accessPath: res.data.accessPath, name: file.name };
    },
    [getUploadCompanyName]
  );

  const onImagesChange = async (e) => {
    if (mediaUploading) {
      return;
    }
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (!picked.length) {
      return;
    }
    const MAX = 20;
    const existing = Array.isArray(f.images) ? f.images : [];
    const maxAdd = Math.max(0, MAX - existing.length);
    if (maxAdd === 0) {
      toast.error('You can add at most 20 images.');
      return;
    }
    const toAdd = picked.slice(0, maxAdd);
    if (picked.length > toAdd.length) {
      toast.info('Only images that fit within the 20-image limit were added.');
    }
    const maxBytes = 10 * 1024 * 1024;
    for (const file of toAdd) {
      if (file.size > maxBytes) {
        toast.error('Each image must be 10MB or less.');
        return;
      }
      if (!/image\/(jpeg|png|jpg)/i.test(file.type)) {
        toast.error('Use PNG or JPG for images.');
        return;
      }
    }
    setMediaUploading(true);
    try {
      const newItems = await uploadBatches(toAdd, 'infrastructure_listing_image');
      setF((prev) => ({
        ...prev,
        images: [...(Array.isArray(prev.images) ? prev.images : []), ...newItems],
      }));
      toast.success(
        toAdd.length === 1
          ? 'Image uploaded'
          : `${toAdd.length} image(s) uploaded`
      );
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Image upload failed';
      toast.error(msg);
    } finally {
      setMediaUploading(false);
    }
  };

  const onVideosChange = async (e) => {
    if (mediaUploading) {
      return;
    }
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (!picked.length) {
      return;
    }
    const MAX_V = 5;
    const existing = Array.isArray(f.videos) ? f.videos : [];
    const maxAdd = Math.max(0, MAX_V - existing.length);
    if (maxAdd === 0) {
      toast.error('You can add at most 5 videos.');
      return;
    }
    const toAdd = picked.slice(0, maxAdd);
    if (picked.length > toAdd.length) {
      toast.info('Only videos that fit within the limit were added.');
    }
    const maxBytes = 200 * 1024 * 1024;
    for (const file of toAdd) {
      if (file.size > maxBytes) {
        toast.error('Each video must be 200MB or less.');
        return;
      }
    }
    setMediaUploading(true);
    try {
      const newItems = await uploadBatches(toAdd, 'infrastructure_listing_video');
      setF((prev) => ({
        ...prev,
        videos: [...(Array.isArray(prev.videos) ? prev.videos : []), ...newItems],
      }));
      toast.success(
        toAdd.length === 1
          ? 'Video uploaded'
          : `${toAdd.length} video(s) uploaded`
      );
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Video upload failed';
      toast.error(msg);
    } finally {
      setMediaUploading(false);
    }
  };

  const onPropertyDocumentChange = (formKey) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || mediaUploading) {
      return;
    }
    const maxBytes = 20 * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error('File must be 20MB or less.');
      return;
    }
    const fileTypes = {
      floorPlan: 'infrastructure_listing_floor_plan',
      drone: 'infrastructure_listing_drone',
      complianceDocs: 'infrastructure_listing_compliance',
    };
    setMediaUploading(true);
    try {
      const item = await uploadOneToCompany(file, fileTypes[formKey] || 'infrastructure_listing_doc');
      setF((prev) => ({ ...prev, [formKey]: item }));
      toast.success('Document uploaded');
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Upload failed';
      toast.error(msg);
    } finally {
      setMediaUploading(false);
    }
  };

  const clearPropertyDocument = (formKey) => () => {
    if (mediaUploading) {
      return;
    }
    up({ [formKey]: null });
  };

  const removeImageAt = (index) => () => {
    setF((prev) => ({
      ...prev,
      images: (Array.isArray(prev.images) ? prev.images : []).filter((_, i) => i !== index),
    }));
  };

  const removeVideoAt = (index) => () => {
    setF((prev) => ({
      ...prev,
      videos: (Array.isArray(prev.videos) ? prev.videos : []).filter((_, i) => i !== index),
    }));
  };

  const saveDraft = useCallback(async () => {
    if (savingDraft) return;
    setSavingDraft(true);
    const serial = serializeInfrastructureListingForm(f);
    const payload = { form: serial, step: String(activeTab) };
    const stored = { form: serial, step: activeTab, savedAt: new Date().toISOString() };
    try {
      localStorage.setItem('infrastructureLeasingListDraft', JSON.stringify(stored));
    } catch {
      toast.error('Could not save draft in browser storage.');
      setSavingDraft(false);
      return;
    }
    try {
      const res = await saveListingDraftAPI(payload);
      if (res?.success !== false) {
        toast.success(res?.message || 'Draft saved');
      } else {
        toast.warning(res?.message || 'Saved on this device; server could not be reached.');
      }
    } catch (e) {
      const msg = e?.response?.data?.message;
      if (e?.response?.status >= 400) {
        toast.error(msg || 'Server rejected the draft. Your copy is saved on this device only.');
      } else {
        toast.warn(msg || 'Draft saved on this device. Could not sync to server.');
      }
    } finally {
      setSavingDraft(false);
    }
  }, [f, activeTab]);

  const onCancel = () => {
    setUnsavedOpen(true);
  };
  const confirmCancel = () => {
    setUnsavedOpen(false);
    navigate('/home');
  };

  const publish = useCallback(async () => {
    if (publishing) return;
    const { valid, errors } = validateInfrastructureListingForPublish(f, {
      subSubOptionsCount: (facilitySubSubTypes || []).length,
    });
    if (!valid) {
      if (errors.length === 1) {
        toast.error(errors[0]);
      } else {
        toast.error(`${errors[0]} (${errors.length} issues — fix the form and try again.)`, { autoClose: 8000 });
      }
      return;
    }
    setPublishing(true);
    const serial = serializeInfrastructureListingForm(f);
    const payload = { form: serial, step: String(activeTab) };
    try {
      localStorage.removeItem('infrastructureLeasingListDraft');
    } catch {
      /* ignore */
    }
    try {
      const res = await publishListingAPI(payload);
      if (res?.success !== false) {
        const listingId = res?.data?.infrastructure_listing_id;
        const isFirstPublish = res?.data?.created === true;
        let inventoryToast = null;
        if (listingId != null && f.inventoryTrackEnabled && isFirstPublish) {
          try {
            const invBody = buildSellerInventoryPostBodyFromListingForm(f);
            const invRes = await postMyPublishedListingInventory(listingId, invBody);
            if (invRes?.success === false) {
              inventoryToast = {
                type: 'warn',
                msg:
                  invRes?.message
                  || 'Listing published, but inventory tracking was not saved. Add inventory from seller tools.',
              };
            }
          } catch (e2) {
            const im = e2?.response?.data?.message || e2?.message;
            inventoryToast = {
              type: 'warn',
              msg:
                im
                || 'Listing published, but inventory tracking could not be saved. You can add inventory later from seller tools.',
            };
          }
        } else if (listingId != null && f.inventoryTrackEnabled && !isFirstPublish) {
          inventoryToast = {
            type: 'info',
            msg:
              'Listing updated. Inventory lines are not changed from this screen — use seller inventory tools to adjust stock.',
          };
        }
        toast.success(res?.message || 'Listing published successfully');
        if (inventoryToast?.type === 'warn') {
          toast.warn(inventoryToast.msg);
        } else if (inventoryToast?.type === 'info') {
          toast.info(inventoryToast.msg);
        }
        setTimeout(() => navigate('/home'), 500);
      } else {
        toast.error(res?.message || 'Could not publish listing');
      }
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.message || e?.message;
      if (status === 413) {
        toast.error('Form is too large for the server. Reduce attachments or try again.');
      } else {
        toast.error(msg || 'Could not publish. Please try again later.');
      }
    } finally {
      setPublishing(false);
    }
  }, [f, activeTab, facilitySubSubTypes, navigate]);

  const loadDraft = useCallback(() => {
    try {
      const raw = localStorage.getItem('infrastructureLeasingListDraft');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed.form) {
        const form = { ...parsed.form };
        const c = form.construction;
        if (c === 'new') form.construction = 'upcoming';
        else if (c === 'ready') form.construction = 'ready_to_move';
        else if (c === 'under') form.construction = 'under_construction';
        if (form.pricingModel === 'sqft' || form.pricingModel === 'negotiable') {
          form.pricingModel = 'monthly_long_lease';
        }
        if (form.durationType && !INFRA_LEASE_DURATION_TYPES.some((o) => o.value === form.durationType)) {
          form.durationType = 'days';
        }
        if (form.equipmentCondition && !/^\d+$/.test(String(form.equipmentCondition))) {
          form.equipmentCondition = '';
        }
        if (form.operationalStatus && !/^\d+$/.test(String(form.operationalStatus))) {
          form.operationalStatus = '';
        }
        if (form.insuranceStatus === 'in_process') {
          form.insuranceStatus = 'insurance_optional';
        }
        if (form.transportResponsibility === 'lessor') {
          form.transportResponsibility = 'seller';
        } else if (form.transportResponsibility === 'lessee') {
          form.transportResponsibility = 'buyer';
        } else if (form.transportResponsibility === 'separate') {
          form.transportResponsibility = 'negotiable';
        }
        const keepMedia = (x) => {
          if (x == null) return false;
          if (typeof x === 'string' && x.trim().length) return true;
          if (typeof x === 'object' && !Array.isArray(x) && x.accessPath && String(x.accessPath).trim()) {
            return true;
          }
          if (x instanceof File) {
            return true;
          }
          return false;
        };
        if (Array.isArray(form.images)) {
          form.images = form.images.filter(keepMedia);
        } else {
          form.images = [];
        }
        if (Array.isArray(form.videos)) {
          form.videos = form.videos.filter(keepMedia);
        } else {
          form.videos = [];
        }
        ['floorPlan', 'drone', 'complianceDocs'].forEach((k) => {
          const v = form[k];
          if (v == null) return;
          if (v && typeof v === 'object' && (v.__isFile || (v.size && v.name && !v.accessPath))) {
            form[k] = null;
            return;
          }
          if (!keepMedia(v)) {
            form[k] = null;
          }
        });
        setF({ ...defaultForm(), ...form });
      }
      if (parsed.step) setActiveTab(String(parsed.step));
    } catch {
      /* ignore */
    }
  }, []);
  useEffect(() => {
    loadDraft();
  }, [loadDraft]);

  const displayListingType = f.listingType === 'property' ? 'Property' : 'Machinery / Asset';
  const displaySize = f.assetSize
    ? PICK_ASSET_SIZES.find((p) => p.id === f.assetSize)?.title || f.assetSize
    : '—';
  const displayTxn = TRANSACTION_TYPE_OPTIONS.find((o) => o.value === f.transactionType)?.label
    || f.transactionType
    || '—';
  const displayLegacy = f.legacyCategory
    ? LEGACY_CATEGORY_OPTIONS.find((o) => o.value === f.legacyCategory)?.label || f.legacyCategory
    : null;
  const displayPropertyType = f.listingType === 'property' && f.propertyType
    ? PROPERTY_TYPE_OPTIONS.find((o) => o.value === f.propertyType)?.label || f.propertyType
    : null;
  const displayFurnishing = f.listingType === 'property' && f.furnishing
    ? FURNISHING_STATUS_OPTIONS.find((o) => o.value === f.furnishing)?.label || f.furnishing
    : null;
  const displayParkingType = f.parkingType
    ? PARKING_TYPE_OPTIONS.find((o) => o.value === f.parkingType)?.label || f.parkingType
    : null;
  const displayConstruction = f.construction
    ? CONSTRUCTION_STATUS_OPTIONS.find((o) => o.value === f.construction)?.label || f.construction
    : null;
  const displayOccupancy = f.occupancy
    ? OCCUPANCY_STATUS_OPTIONS.find((o) => o.value === f.occupancy)?.label || f.occupancy
    : null;
  const durationTypeUnit = INFRA_LEASE_DURATION_TYPES.find((o) => o.value === f.durationType)?.value || 'period';

  return (
    <div className="ile-list-asset-page">
      <div className="seller-profile contract-manufacturer-profile infrastructure-leasing-asset container-fluid">
        <div className="row">
          <main className="col-12 sp-main" style={{ paddingTop: 16 }}>
            <div className="sp-header-sticky">
              <nav aria-label="breadcrumb" className="sp-breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/home" className="breadcrumb-link">Home</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">List your asset</li>
                </ol>
              </nav>
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-2">
                <div>
                  <div className="ile-ie-header">
                    <IleIcon />
                    <div>
                      <h1 className="ile-ie-title" style={{ margin: 0 }}>Infrastructure &amp; Asset Exchange</h1>
                      <p className="sp-subtitle m-0">Create New Listing</p>
                    </div>
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <button type="button" className="btn btn-outline-primary" onClick={saveDraft} disabled={savingDraft || publishing || mediaUploading}>
                    <FaSave className="me-1" />
                    {savingDraft ? 'Saving…' : 'Save as Draft'}
                  </button>
                  <button type="button" className="btn btn-outline-primary" onClick={onCancel}>
                    Cancel
                  </button>
                </div>
              </div>

              <div>
                <div className="ile-progress">
                  <span>Step {stepNum} of 6</span>
                  <span>{progressPct}% Complete</span>
                </div>
                <div className="ile-progress-bar" role="progressbar" aria-valuenow={progressPct} aria-valuemin={0} aria-valuemax={100}>
                  <div className="ile-progress-bar-fill" style={{ width: `${progressPct}%` }} />
                </div>
              </div>
            </div>

            <Tabs
              className="ile-vtabs"
              style={{ marginTop: 8 }}
              activeKey={activeTab}
              onChange={setActiveTab}
              tabPosition="left"
            >
              <TabPane
                tab={(
                  <span>
                    {tabIcon('M2 20h20v-8H2v8M6 20v-4M10 20v-4M14 20v-4M18 20v-4M2 12h20V8l-10-4-10 4v4Z')}
                    Basic
                  </span>
                )}
                key="1"
              >
                <div className="container-fluid p-0">
                  <div className="sp-subcard">
                    <h6 className="sp-section-title">Basic information</h6>
                    <p className="ile-mute">Fill in all required fields to create your listing</p>
                    <div className="mb-3">
                      <span className="ile-form-label d-block mb-2">Listing Type</span>
                      <div className="ile-pick-row ile-pick-row--listing-type">
                        {LISTING_TYPE_OPTIONS.map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            className={`ile-pick-card ile-pick-card--listing${
                              f.listingType === opt.id ? ' ile-pick-card--on' : ''
                            }`}
                            onClick={() => up({ listingType: opt.id, assetCategory: '', subCategory: '', subSubCategory: '' })}
                          >
                            <span className="ile-pick-icon ile-pick-icon--listing" aria-hidden>
                              <ListingTypeBuildingIcon />
                            </span>
                            <span className="ile-pick-title">{opt.title}</span>
                            <span className="ile-pick-sub">{opt.sub}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="mb-2">
                      <span className="ile-form-label">Listing title *</span>
                      <input className="input-pill" placeholder="e.g., Premium Commercial Warehouse - 50,000 Sq Ft" value={f.listingTitle} onChange={setField('listingTitle')} />
                    </div>
                    <div className="row g-2 align-items-end">
                      <div
                        className={`col-12 ${
                          f.listingType === 'machinery' ? 'col-lg-4' : 'col-lg-6'
                        }`}
                      >
                        <span className="ile-form-label">Asset category *</span>
                        <select
                          className="input-pill"
                          value={f.assetCategory}
                          onChange={onAssetCategoryChange}
                          disabled={loadingFacilityTypes}
                        >
                          <option value="">
                            {loadingFacilityTypes ? 'Loading categories...' : 'Select category'}
                          </option>
                          {facilityTypes.map((row) => (
                            <option
                              key={row.facility_type_id}
                              value={String(row.facility_type_id)}
                            >
                              {row.facility_type}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div
                        className={`col-12 ${
                          f.listingType === 'machinery' ? 'col-lg-4' : 'col-lg-6'
                        }`}
                      >
                        <span className="ile-form-label">Sub-category *</span>
                        <select
                          className="input-pill"
                          value={f.subCategory}
                          onChange={onSubCategoryChange}
                          disabled={!f.assetCategory || loadingSubTypes}
                        >
                          <option value="">
                            {!f.assetCategory
                              ? 'Select asset category first'
                              : loadingSubTypes
                                ? 'Loading sub-categories...'
                                : 'Select sub-category'}
                          </option>
                          {facilitySubTypes.map((row) => (
                            <option
                              key={row.facility_sub_type_id}
                              value={String(row.facility_sub_type_id)}
                            >
                              {row.facility_sub_type}
                            </option>
                          ))}
                        </select>
                      </div>
                      {f.listingType === 'machinery' && (
                        <div className="col-12 col-lg-4">
                          <span className="ile-form-label">Sub-sub-category *</span>
                          <select
                            className="input-pill"
                            value={f.subSubCategory}
                            onChange={setField('subSubCategory')}
                            disabled={!f.subCategory || loadingSubSubTypes}
                            required={f.listingType === 'machinery' && !loadingSubSubTypes && facilitySubSubTypes.length > 0}
                            aria-required={f.listingType === 'machinery' && facilitySubSubTypes.length > 0}
                          >
                            <option value="">
                              {!f.subCategory
                                ? 'Select sub-category first'
                                : loadingSubSubTypes
                                  ? 'Loading…'
                                  : 'Select sub-sub-category'}
                            </option>
                            {facilitySubSubTypes.map((row) => (
                              <option
                                key={row.facility_sub_sub_type_id}
                                value={String(row.facility_sub_sub_type_id)}
                              >
                                {row.facility_sub_sub_type}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                    <div className="row g-2 align-items-end mt-0">
                      <div className="col-12 col-lg-4">
                        <span className="ile-form-label">Legacy category (optional)</span>
                        <select className="input-pill" value={f.legacyCategory} onChange={setField('legacyCategory')}>
                          <option value="">Select category</option>
                          {LEGACY_CATEGORY_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-lg-4">
                        <span className="ile-form-label">Ownership type *</span>
                        <select className="input-pill" value={f.ownershipType} onChange={setField('ownershipType')}>
                          {OWNERSHIP_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-lg-4">
                        <span className="ile-form-label">Transaction type *</span>
                        <select className="input-pill" value={f.transactionType} onChange={setField('transactionType')}>
                          {TRANSACTION_TYPE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <hr className="ile-section-divider" />
                    <span className="ile-form-label d-block">Asset size category</span>
                    <div className="ile-pick-row ile-pick-row--3">
                      {PICK_ASSET_SIZES.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          className={`ile-pick-card${f.assetSize === p.id ? ' ile-pick-card--on' : ''}`}
                          onClick={() => up({ assetSize: p.id })}
                        >
                          <span className="ile-pick-title">{p.title}</span>
                          <span className="ile-pick-sub">{p.sub}</span>
                        </button>
                      ))}
                    </div>
                    {(f.assetSize === 'small' || f.assetSize === 'micro') && (
                      <div className="mb-0 mt-3">
                        <span className="ile-form-label">Quantity Available</span>
                        <input
                          className="input-pill"
                          type="number"
                          min={1}
                          step={1}
                          inputMode="numeric"
                          value={f.quantityAvailable}
                          onChange={setField('quantityAvailable')}
                          placeholder="1"
                        />
                        <p className="ile-mute small mt-1 mb-0">How many units are available? (e.g., 10 drills, 5 laptops)</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="d-flex flex-wrap justify-content-between align-items-center sp-actions-row px-1 pb-2" style={{ marginTop: 12 }}>
                  <button type="button" className="ile-btn-ghost" onClick={goPrev} disabled>
                    <FaChevronLeft size={12} />
                    Previous
                  </button>
                  <div className="d-flex flex-wrap gap-2">
                    <button type="button" className="ile-btn-ghost" onClick={saveDraft} disabled={savingDraft || publishing || mediaUploading}><FaSave className="me-1" />{savingDraft ? 'Saving…' : 'Save as Draft'}</button>
                    <button type="button" className="ile-btn-next" onClick={goNext}>
                      Next
                      <FaChevronRight size={12} />
                    </button>
                  </div>
                </div>
              </TabPane>

              <TabPane
                tab={(
                  <span>
                    {tabIcon('M9 2h6M12 2v2M3 5h18v15a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5zM3 8h18M9 12h6M9 16h2')}
                    Location
                  </span>
                )}
                key="2"
              >
                <div className="container-fluid p-0">
                  <div className="sp-subcard">
                    <h6 className="sp-section-title">Location details</h6>
                    <p className="ile-mute">Fill in all required fields to create your listing</p>
                    <div className="row g-2 align-items-end mb-2">
                      <div className="col-12 col-lg-4">
                        <span className="ile-form-label">Country *</span>
                        <select
                          className="input-pill"
                          value={f.countryId}
                          onChange={onCountry}
                          disabled={loading}
                        >
                          <option value="">{loading ? 'Loading...' : 'Select country'}</option>
                          {countryOptions.map((c) => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-lg-4">
                        <span className="ile-form-label">State / province *</span>
                        <select className="input-pill" value={f.stateId} onChange={onState} disabled={!f.countryId || loadingStates}>
                          <option value="">{!f.countryId ? 'Select country first' : loadingStates ? 'Loading...' : 'Select state'}</option>
                          {stateOptions.map((c) => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-lg-4">
                        <span className="ile-form-label">City / district *</span>
                        <select className="input-pill" value={f.cityId} onChange={setField('cityId')} disabled={!f.stateId || loadingCities}>
                          <option value="">{!f.stateId ? 'Select state first' : loadingCities ? 'Loading...' : 'Select city'}</option>
                          {cityOptions.map((c) => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="mb-2">
                      <span className="ile-form-label">Full address &amp; landmarks *</span>
                      <textarea
                        className="input-pill"
                        style={{ minHeight: 100 }}
                        placeholder="Complete address with street name, building number, landmarks, nearby areas…"
                        value={f.fullAddress}
                        onChange={setField('fullAddress')}
                      />
                    </div>
                    <div className="row g-2">
                      <div className="col-12 col-lg-6">
                        <span className="ile-form-label">Postal / PIN code</span>
                        <input className="input-pill" placeholder="e.g., 400001, 10001, SW1A 1AA" value={f.postalCode} onChange={setField('postalCode')} />
                      </div>
                      <div className="col-12 col-lg-6">
                        <span className="ile-form-label">Industrial zone / special area</span>
                        <input className="input-pill" placeholder="e.g., MIDC, SEZ, Free Zone, Industrial Park" value={f.industrialZone} onChange={setField('industrialZone')} />
                      </div>
                    </div>
                    <div className="p-2 ile-banner rounded mt-3" style={{ fontSize: '0.85rem' }}>
                      {f.countryId
                        ? 'State/province and city/district are enabled. Complete all location fields to continue.'
                        : 'Please select a country to enable state/province and city/district selection.'}
                    </div>
                  </div>
                </div>
                <div className="d-flex flex-wrap justify-content-between align-items-center sp-actions-row px-1 pb-2" style={{ marginTop: 12 }}>
                  <button type="button" className="ile-btn-ghost" onClick={goPrev}><FaChevronLeft size={12} />Previous</button>
                  <div className="d-flex flex-wrap gap-2">
                    <button type="button" className="ile-btn-ghost" onClick={saveDraft} disabled={savingDraft || publishing || mediaUploading}><FaSave className="me-1" />{savingDraft ? 'Saving…' : 'Save as Draft'}</button>
                    <button type="button" className="ile-btn-next" onClick={goNext}>Next<FaChevronRight size={12} /></button>
                  </div>
                </div>
              </TabPane>

              <TabPane
                tab={(
                  <span>
                    {tabIcon('M2 2h4v4H2V2zm0 6h4v4H2V8zm0 6h4v4H2v-4zM8 2h4v2H8V2zm0 3h3v1H8V5zm0 2h2v1H8V7z')}
                    {f.listingType === 'property' ? 'Property' : 'Asset'}
                  </span>
                )}
                key="3"
              >
                <div className="container-fluid p-0">
                  <div className="sp-subcard">
                    <h6 className="sp-section-title">{f.listingType === 'property' ? 'Property specifications' : 'Asset specifications'}</h6>
                    <p className="ile-mute">Fill in all required fields to create your listing</p>
                    {f.listingType === 'property' && (
                      <>
                        <div className="mb-2">
                          <span className="ile-form-label">Property type *</span>
                          <select className="input-pill" value={f.propertyType} onChange={setField('propertyType')}>
                            <option value="">Select property type</option>
                            {PROPERTY_TYPE_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="row g-2">
                          <div className="col-12 col-lg-4">
                            <span className="ile-form-label">Carpet area (sq ft) *</span>
                            <input className="input-pill" type="number" value={f.carpetArea} onChange={setField('carpetArea')} placeholder="e.g. 5000" />
                          </div>
                          <div className="col-12 col-lg-4">
                            <span className="ile-form-label">Built-up area (sq ft)</span>
                            <input className="input-pill" type="number" value={f.builtUpArea} onChange={setField('builtUpArea')} />
                          </div>
                          <div className="col-12 col-lg-4">
                            <span className="ile-form-label">Land area (sq ft / acres)</span>
                            <input
                              className="input-pill"
                              type="number"
                              min={0}
                              step="any"
                              inputMode="decimal"
                              value={f.landArea}
                              onChange={setField('landArea')}
                              placeholder="e.g. 1200"
                            />
                          </div>
                        </div>
                        <div className="row g-2 mt-1">
                          <div className="col-12 col-lg-6">
                            <span className="ile-form-label">Zoning / usage type</span>
                            <input className="input-pill" placeholder="e.g. Commercial, Industrial" value={f.zoning} onChange={setField('zoning')} />
                          </div>
                          <div className="col-12 col-lg-6">
                            <span className="ile-form-label">Furnishing status</span>
                            <select className="input-pill" value={f.furnishing} onChange={setField('furnishing')}>
                              <option value="">Select status</option>
                              {FURNISHING_STATUS_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </>
                    )}
                    {f.listingType === 'machinery' && (
                      <>
                        <div className="row g-2">
                          <div className="col-12 col-lg-6">
                            <span className="ile-form-label">Brand *</span>
                            <input
                              className="input-pill"
                              value={f.equipmentBrand}
                              onChange={setField('equipmentBrand')}
                              placeholder="e.g., Caterpillar, Kirloskar"
                            />
                          </div>
                          <div className="col-12 col-lg-6">
                            <span className="ile-form-label">Model *</span>
                            <input
                              className="input-pill"
                              value={f.equipmentModel}
                              onChange={setField('equipmentModel')}
                              placeholder="e.g., 320D, 4KVA"
                            />
                          </div>
                        </div>
                        <div className="row g-2 mt-1">
                          <div className="col-12 col-lg-4">
                            <span className="ile-form-label">Make *</span>
                            <input className="input-pill" value={f.equipmentMake} onChange={setField('equipmentMake')} placeholder="Manufacturer / make" />
                          </div>
                          <div className="col-12 col-lg-4">
                            <span className="ile-form-label">Serial number *</span>
                            <input className="input-pill" value={f.serialNumber} onChange={setField('serialNumber')} placeholder="Chassis / asset tag" />
                          </div>
                          <div className="col-12 col-lg-4">
                            <span className="ile-form-label">Year *</span>
                            <input
                              className="input-pill"
                              type="number"
                              value={f.manufactureYear}
                              onChange={setField('manufactureYear')}
                              placeholder="e.g., 2019"
                              min={1980}
                              max={new Date().getFullYear() + 1}
                            />
                          </div>
                        </div>
                        <div className="row g-2 mt-1">
                          <div className="col-12 col-lg-6">
                            <span className="ile-form-label">Condition *</span>
                            <select
                              className="input-pill"
                              value={f.equipmentCondition}
                              onChange={setField('equipmentCondition')}
                              disabled={loadingConditionFacility}
                            >
                              <option value="">{loadingConditionFacility ? 'Loading…' : 'Select condition'}</option>
                              {conditionFacilities.map((row) => (
                                <option
                                  key={row.condition_facility_id}
                                  value={String(row.condition_facility_id)}
                                >
                                  {row.condition_facility}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-12 col-lg-6">
                            <span className="ile-form-label">Status *</span>
                            <select
                              className="input-pill"
                              value={f.operationalStatus}
                              onChange={setField('operationalStatus')}
                              disabled={loadingOperationalStatus}
                            >
                              <option value="">{loadingOperationalStatus ? 'Loading…' : 'Select status'}</option>
                              {operationalStatuses.map((row) => (
                                <option
                                  key={row.operational_status_id}
                                  value={String(row.operational_status_id)}
                                >
                                  {row.operational_status}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="mb-2 mt-1">
                          <span className="ile-form-label">Usage hours *</span>
                          <input
                            className="input-pill"
                            type="number"
                            min={0}
                            value={f.usageHours}
                            onChange={setField('usageHours')}
                            placeholder="Cumulative hours on meter"
                          />
                        </div>

                        <hr className="ile-section-divider" />
                        <h6 className="sp-section-title" style={{ marginTop: 12 }}>Capacity &amp; Dimensions</h6>
                        <div className="row g-2">
                          <div className="col-12 col-lg-6">
                            <span className="ile-form-label">Max capacity *</span>
                            <input
                              className="input-pill"
                              type="number"
                              min={0}
                              step="any"
                              value={f.maxCapacity}
                              onChange={setField('maxCapacity')}
                              placeholder="e.g., 5000"
                            />
                          </div>
                          <div className="col-12 col-lg-6">
                            <span className="ile-form-label">Unit of measure</span>
                            <input
                              className="input-pill"
                              value={f.capacityUnit}
                              onChange={setField('capacityUnit')}
                              placeholder="e.g., L, HP, ton/hr, kW"
                            />
                          </div>
                        </div>
                        <div className="row g-2 mt-1">
                          <div className="col-12 col-lg-4">
                            <span className="ile-form-label">Length</span>
                            <input
                              className="input-pill"
                              type="number"
                              min={0}
                              step="any"
                              value={f.dimLength}
                              onChange={setField('dimLength')}
                              placeholder="0"
                            />
                          </div>
                          <div className="col-12 col-lg-4">
                            <span className="ile-form-label">Width</span>
                            <input
                              className="input-pill"
                              type="number"
                              min={0}
                              step="any"
                              value={f.dimWidth}
                              onChange={setField('dimWidth')}
                              placeholder="0"
                            />
                          </div>
                          <div className="col-12 col-lg-4">
                            <span className="ile-form-label">Height</span>
                            <input
                              className="input-pill"
                              type="number"
                              min={0}
                              step="any"
                              value={f.dimHeight}
                              onChange={setField('dimHeight')}
                              placeholder="0"
                            />
                          </div>
                        </div>
                        <p className="ile-mute small mb-1 mt-0">Enter dimensions in metres (m) or note unit in the description if different.</p>
                        <div className="row g-2">
                          <div className="col-12 col-lg-6">
                            <span className="ile-form-label">Net weight</span>
                            <input
                              className="input-pill"
                              type="number"
                              min={0}
                              step="any"
                              value={f.netWeight}
                              onChange={setField('netWeight')}
                              placeholder="0"
                            />
                          </div>
                          <div className="col-12 col-lg-6">
                            <span className="ile-form-label">Weight unit *</span>
                            <select className="input-pill" value={f.weightUnit} onChange={setField('weightUnit')}>
                              <option value="">Select weight unit</option>
                              {MACHINERY_WEIGHT_UNIT_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <hr className="ile-section-divider" />
                        <h6 className="sp-section-title" style={{ marginTop: 12 }}>Location &amp; Mobility</h6>
                        <div className="mb-2">
                          <span className="ile-form-label">Current installation location *</span>
                          <input
                            className="input-pill"
                            value={f.installationLocation}
                            onChange={setField('installationLocation')}
                            placeholder="Site / plant, city, state"
                          />
                        </div>
                        <div className="row g-2 g-md-3 align-items-end">
                          <div className={`col-12 ${f.relocationPossible ? 'col-lg-6' : 'col-12'}`}>
                            <label className="ile-infra-check">
                              <input type="checkbox" checked={f.relocationPossible} onChange={onRelocationToggle} />
                              <span>Relocation possible</span>
                            </label>
                          </div>
                          {f.relocationPossible && (
                            <div className="col-12 col-lg-6">
                              <span className="ile-form-label">Relocation cost (₹)</span>
                              <input
                                className="input-pill"
                                type="number"
                                min={0}
                                value={f.relocationCost}
                                onChange={setField('relocationCost')}
                                placeholder="0"
                              />
                            </div>
                          )}
                        </div>

                        <hr className="ile-section-divider" />
                        <h6 className="sp-section-title" style={{ marginTop: 12 }}>Operations</h6>
                        <div className="row g-2 g-md-3 align-items-end">
                          <div className={`col-12 ${f.operatorIncluded ? 'col-lg-6' : 'col-12'}`}>
                            <label className="ile-infra-check">
                              <input type="checkbox" checked={f.operatorIncluded} onChange={onOperatorToggle} />
                              <span>Operator included</span>
                            </label>
                          </div>
                          {f.operatorIncluded && (
                            <div className="col-12 col-lg-6">
                              <span className="ile-form-label">Operator add-on (₹) / month</span>
                              <input
                                className="input-pill"
                                type="number"
                                min={0}
                                value={f.operatorAddOnCost}
                                onChange={setField('operatorAddOnCost')}
                                placeholder="0"
                              />
                            </div>
                          )}
                        </div>
                        <div className="row g-2 mt-1">
                          <div className="col-12 col-lg-6">
                            <span className="ile-form-label">Fuel type</span>
                            <input
                              className="input-pill"
                              value={f.fuelType}
                              onChange={setField('fuelType')}
                              placeholder="Diesel, Petrol, Electric, hybrid, N/A"
                            />
                          </div>
                          <div className="col-12 col-lg-6">
                            <span className="ile-form-label">Fuel consumption</span>
                            <input
                              className="input-pill"
                              value={f.fuelConsumption}
                              onChange={setField('fuelConsumption')}
                              placeholder="e.g., 12 L/hr, kWh/100h"
                            />
                          </div>
                        </div>

                        <hr className="ile-section-divider" />
                        <h6 className="sp-section-title" style={{ marginTop: 12 }}>Insurance &amp; Transport</h6>
                        <div className="row g-2">
                          <div className="col-12 col-lg-6">
                            <span className="ile-form-label">Insurance Status</span>
                            <select className="input-pill" value={f.insuranceStatus} onChange={setField('insuranceStatus')}>
                              {MACHINERY_INSURANCE_STATUS_OPTIONS.map((o) => (
                                <option key={o.value || 'x'} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="col-12 col-lg-6">
                            <span className="ile-form-label">Insurance Coverage (₹)</span>
                            <input
                              className="input-pill"
                              type="number"
                              min={0}
                              value={f.insuranceCost}
                              onChange={setField('insuranceCost')}
                              placeholder="0"
                            />
                          </div>
                        </div>
                        <div className="row g-2 mt-1">
                          <div className="col-12 col-lg-6">
                            <span className="ile-form-label">Transport Responsibility</span>
                            <select className="input-pill" value={f.transportResponsibility} onChange={setField('transportResponsibility')}>
                              {MACHINERY_TRANSPORT_BY_OPTIONS.map((o) => (
                                <option key={o.value || 'z'} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="col-12 col-lg-6">
                            <span className="ile-form-label">Transport Cost (₹)</span>
                            <input
                              className="input-pill"
                              type="number"
                              min={0}
                              value={f.transportCost}
                              onChange={setField('transportCost')}
                              placeholder="0"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {f.listingType === 'property' && (
                    <>
                    <h6 className="sp-section-title" style={{ marginTop: 16 }}>Infrastructure &amp; Utilities</h6>
                    <div className="ile-infra-utils">
                      <div className="row g-2 g-md-3 align-items-end ile-infra-utils-row">
                        <div className={`col-12 ${f.power ? 'col-lg-6' : 'col-12'}`}>
                          <label className="ile-infra-check">
                            <input type="checkbox" checked={f.power} onChange={onPowerToggle} />
                            <span>Power Availability</span>
                          </label>
                        </div>
                        {f.power && (
                          <div className="col-12 col-lg-6">
                            <span className="ile-form-label">Power Load (KW)</span>
                            <input
                              className="input-pill"
                              type="number"
                              value={f.powerLoadKw}
                              onChange={setField('powerLoadKw')}
                              placeholder="100"
                              min={0}
                            />
                          </div>
                        )}
                      </div>
                      <div className="row g-2 g-md-3 align-items-center ile-infra-utils-row">
                        <div className="col-12 col-lg-6">
                          <label className="ile-infra-check">
                            <input type="checkbox" checked={f.water} onChange={setChecked('water')} />
                            <span>Water Availability</span>
                          </label>
                        </div>
                        <div className="col-12 col-lg-6">
                          <label className="ile-infra-check">
                            <input type="checkbox" checked={f.loading} onChange={setChecked('loading')} />
                            <span>Loading/Unloading Facility</span>
                          </label>
                        </div>
                      </div>
                      <div className="row g-2 align-items-end ile-infra-utils-row">
                        <div className="col-12 col-lg-4">
                          <span className="ile-form-label">Parking Spaces</span>
                          <input
                            className="input-pill"
                            type="number"
                            value={f.parking}
                            onChange={setField('parking')}
                            placeholder="10"
                            min={0}
                          />
                        </div>
                        <div className="col-12 col-lg-4">
                          <span className="ile-form-label">Parking Type</span>
                          <select className="input-pill" value={f.parkingType} onChange={setField('parkingType')}>
                            <option value="">Select type</option>
                            {PARKING_TYPE_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-12 col-lg-4">
                          <span className="ile-form-label">Road Access (feet)</span>
                          <input
                            className="input-pill"
                            type="number"
                            value={f.roadAccessFt}
                            onChange={setField('roadAccessFt')}
                            placeholder="40"
                            min={0}
                          />
                        </div>
                      </div>
                    </div>

                    <h6 className="sp-section-title" style={{ marginTop: 16 }}>Compliance &amp; Approvals</h6>
                    <div className="row g-1">
                      <div className="col-12 col-lg-6">
                        <label className="ile-radio"><input type="checkbox" checked={f.cFire} onChange={setChecked('cFire')} /> Fire safety</label>
                        <label className="ile-radio"><input type="checkbox" checked={f.cEnv} onChange={setChecked('cEnv')} /> Environmental clearance</label>
                        <label className="ile-radio"><input type="checkbox" checked={f.cTrade} onChange={setChecked('cTrade')} /> Trade license</label>
                      </div>
                      <div className="col-12 col-lg-6">
                        <label className="ile-radio"><input type="checkbox" checked={f.cOcc} onChange={setChecked('cOcc')} /> Occupancy certificate</label>
                        <label className="ile-radio"><input type="checkbox" checked={f.cPol} onChange={setChecked('cPol')} /> Pollution clearance</label>
                      </div>
                    </div>

                    <h6 className="sp-section-title" style={{ marginTop: 16 }}>Status</h6>
                    <div className="row g-2">
                      <div className="col-12 col-lg-6">
                        <span className="ile-form-label">Construction status</span>
                        <select className="input-pill" value={f.construction} onChange={setField('construction')}>
                          <option value="">Select status</option>
                          {CONSTRUCTION_STATUS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-lg-6">
                        <span className="ile-form-label">Occupancy status</span>
                        <select className="input-pill" value={f.occupancy} onChange={setField('occupancy')}>
                          <option value="">Select status</option>
                          {OCCUPANCY_STATUS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    </>
                    )}
                  </div>
                </div>
                <div className="d-flex flex-wrap justify-content-between align-items-center sp-actions-row px-1 pb-2" style={{ marginTop: 12 }}>
                  <button type="button" className="ile-btn-ghost" onClick={goPrev}><FaChevronLeft size={12} />Previous</button>
                  <div className="d-flex flex-wrap gap-2">
                    <button type="button" className="ile-btn-ghost" onClick={saveDraft} disabled={savingDraft || publishing || mediaUploading}><FaSave className="me-1" />{savingDraft ? 'Saving…' : 'Save as Draft'}</button>
                    <button type="button" className="ile-btn-next" onClick={goNext}>Next<FaChevronRight size={12} /></button>
                  </div>
                </div>
              </TabPane>

              <TabPane
                tab={(
                  <span>
                    {tabIcon('M7 2v20M2 5h3M2 12h3M2 19h3M17 2v20M22 5h-3M22 12h-3M22 19h-3')}
                    Pricing
                  </span>
                )}
                key="4"
              >
                <div className="container-fluid p-0">
                  <div className="sp-subcard">
                    <h6 className="sp-section-title">Pricing &amp; terms</h6>
                    <p className="ile-mute">Fill in all required fields to create your listing</p>
                    <div className="row g-2 align-items-start mb-2">
                      <div className="col-12 col-lg-6">
                        <span className="ile-form-label">Pricing Model</span>
                        <select className="input-pill" value={f.pricingModel} onChange={setField('pricingModel')}>
                          {PRICING_MODEL_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                        <p className="ile-mute" style={{ marginTop: 4 }}>Choose the model that best fits your rental/lease</p>
                      </div>
                      <div className="col-12 col-lg-6">
                        <span className="ile-form-label">Rent per month (₹) *</span>
                        <input className="input-pill" type="number" value={f.rentPerMonth} onChange={setField('rentPerMonth')} />
                      </div>
                    </div>
                    <div className="row g-2">
                      <div className="col-12 col-lg-6">
                        <span className="ile-form-label">Deposit / security (₹)</span>
                        <input className="input-pill" type="number" value={f.deposit} onChange={setField('deposit')} />
                      </div>
                      <div className="col-12 col-lg-6">
                        <span className="ile-form-label">Maintenance charges (₹/month)</span>
                        <input className="input-pill" type="number" value={f.maintenance} onChange={setField('maintenance')} />
                      </div>
                    </div>
                    <h6 className="sp-section-title" style={{ marginTop: 16 }}>Lease / Rental Duration</h6>
                    <div className="row g-2 align-items-start">
                      <div className="col-12 col-lg-6">
                        <span className="ile-form-label">Duration Type</span>
                        <select className="input-pill" value={f.durationType} onChange={setField('durationType')}>
                          {INFRA_LEASE_DURATION_TYPES.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-lg-6">
                        <span className="ile-form-label">Available for (total)</span>
                        <input className="input-pill" type="number" value={f.availableDuration} onChange={setField('availableDuration')} />
                        <p className="ile-mute" style={{ marginTop: 4 }}>How long can this be rented/leased?</p>
                      </div>
                    </div>
                    <div className="row g-2">
                      <div className="col-12 col-lg-4">
                        <span className="ile-form-label">Minimum tenure</span>
                        <input className="input-pill" type="number" value={f.minTenure} onChange={setField('minTenure')} />
                        <p className="ile-mute" style={{ marginTop: 4 }}>in {durationTypeUnit}</p>
                      </div>
                      <div className="col-12 col-lg-4">
                        <span className="ile-form-label">Maximum tenure</span>
                        <input className="input-pill" type="number" value={f.maxTenure} onChange={setField('maxTenure')} />
                      </div>
                      <div className="col-12 col-lg-4">
                        <span className="ile-form-label">Lock-in period</span>
                        <input className="input-pill" type="number" value={f.lockin} onChange={setField('lockin')} />
                        <p className="ile-mute" style={{ marginTop: 4 }}>in {durationTypeUnit}</p>
                      </div>
                    </div>
                    <label className="d-flex align-items-center gap-2 my-2 small">
                      <input type="checkbox" checked={f.flexibleTenure} onChange={setChecked('flexibleTenure')} />
                      Tenure is flexible and can be extended based on requirements
                    </label>
                    <div className="ile-callout">Examples: 6–12 months for equipment, 1–3 years for small offices, 3+ years for factories — adjust to your asset class.</div>
                    <h6 className="sp-section-title" style={{ marginTop: 16 }}>Availability</h6>
                    <div className="row g-2">
                      <div className="col-12 col-lg-4">
                        <span className="ile-form-label">Available from *</span>
                        <input className="input-pill" type="date" value={f.availableFrom} onChange={setField('availableFrom')} />
                      </div>
                    </div>

                    <h6 className="sp-section-title" style={{ marginTop: 20 }}>Inventory &amp; availability (optional)</h6>
                    <p className="ile-mute small mb-2">
                      Enable tracked inventory so Trocanet can show real availability, partial leasing, and booked periods on your listing.
                      On first publish only, we create one default inventory line from the fields below. Maintenance blocks and extra lines can be added later from seller tools.
                    </p>
                    <label className="d-flex align-items-center gap-2 mb-3 small">
                      <input
                        type="checkbox"
                        checked={Boolean(f.inventoryTrackEnabled)}
                        onChange={(e) => up({ inventoryTrackEnabled: e.target.checked })}
                      />
                      Track inventory for this listing
                    </label>
                    {f.inventoryTrackEnabled && (
                      <div className="row g-2 align-items-start">
                        <div className="col-12 col-lg-6">
                          <span className="ile-form-label">Inventory model</span>
                          <select
                            className="input-pill"
                            value={f.inventoryKind}
                            onChange={setField('inventoryKind')}
                          >
                            {INVENTORY_MODEL_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-12 col-lg-6">
                          <span className="ile-form-label">Line label (optional)</span>
                          <input
                            className="input-pill"
                            placeholder="e.g. Hall A, Fleet pool"
                            value={f.inventoryLabel}
                            onChange={setField('inventoryLabel')}
                          />
                        </div>
                        {(f.inventoryKind === 'unit' || f.inventoryKind === 'hybrid') && (
                          <div className="col-12 col-md-4">
                            <span className="ile-form-label">
                              {f.inventoryKind === 'hybrid' ? 'Total units (if applicable)' : 'Total units *'}
                            </span>
                            <input className="input-pill" type="number" min="0" step="1" value={f.inventoryTotalUnits} onChange={setField('inventoryTotalUnits')} />
                          </div>
                        )}
                        {(f.inventoryKind === 'area' || f.inventoryKind === 'time' || f.inventoryKind === 'hybrid') && (
                          <div className="col-12 col-md-4">
                            <span className="ile-form-label">
                              {f.inventoryKind === 'time' ? 'Total leasable area (sq ft) *' : f.inventoryKind === 'hybrid' ? 'Total area (sq ft)' : 'Total area (sq ft) *'}
                            </span>
                            <input className="input-pill" type="number" min="0" step="0.01" value={f.inventoryTotalArea} onChange={setField('inventoryTotalArea')} />
                          </div>
                        )}
                        {(f.inventoryKind === 'capacity' || f.inventoryKind === 'hybrid') && (
                          <div className="col-12 col-md-4">
                            <span className="ile-form-label">
                              {f.inventoryKind === 'hybrid' ? 'Total capacity (if applicable)' : 'Total capacity *'}
                            </span>
                            <input className="input-pill" type="number" min="0" step="0.01" value={f.inventoryTotalCapacity} onChange={setField('inventoryTotalCapacity')} />
                          </div>
                        )}
                        {f.inventoryKind === 'capacity' && (
                          <div className="col-12 col-md-4">
                            <span className="ile-form-label">Capacity unit *</span>
                            <input className="input-pill" placeholder="e.g. tonnes, pallets" value={f.inventoryCapacityUom} onChange={setField('inventoryCapacityUom')} />
                          </div>
                        )}
                        {f.inventoryKind === 'hybrid' && String(f.inventoryTotalCapacity || '').trim() && (
                          <div className="col-12 col-md-4">
                            <span className="ile-form-label">Capacity unit</span>
                            <input className="input-pill" placeholder="e.g. tonnes" value={f.inventoryCapacityUom} onChange={setField('inventoryCapacityUom')} />
                          </div>
                        )}
                        <div className="col-12">
                          <label className="d-flex align-items-center gap-2 small mb-0">
                            <input
                              type="checkbox"
                              checked={Boolean(f.inventoryPartialLeasingAllowed)}
                              onChange={(e) => up({ inventoryPartialLeasingAllowed: e.target.checked })}
                            />
                            Partial leasing / letting allowed (buyers may take a fraction of the total)
                          </label>
                        </div>
                        <div className="col-12 col-md-4">
                          <span className="ile-form-label">Minimum leasable quantity</span>
                          <input className="input-pill" type="number" min="0" step="1" value={f.inventoryMinLeasableUnits} onChange={setField('inventoryMinLeasableUnits')} />
                          <p className="ile-mute mb-0" style={{ marginTop: 4 }}>Units; informational for buyers</p>
                        </div>
                        <div className="col-12 col-md-4">
                          <span className="ile-form-label">Minimum leasable area (sq ft)</span>
                          <input className="input-pill" type="number" min="0" step="0.01" value={f.inventoryMinLeasableArea} onChange={setField('inventoryMinLeasableArea')} />
                        </div>
                        <div className="col-12 col-md-4">
                          <span className="ile-form-label">Minimum duration ({durationTypeUnit})</span>
                          <input className="input-pill" type="number" min="0" step="1" value={f.inventoryMinLeasableDuration} onChange={setField('inventoryMinLeasableDuration')} />
                          <p className="ile-mute mb-0" style={{ marginTop: 4 }}>Shown with listing terms (not stored on inventory row)</p>
                        </div>
                        {f.inventoryKind === 'hybrid' && (
                          <div className="col-12">
                            <span className="ile-form-label">Hybrid notes</span>
                            <textarea
                              className="input-pill"
                              style={{ minHeight: 72 }}
                              placeholder="Describe how dimensions combine (stored with inventory metadata)."
                              value={f.inventoryHybridNote}
                              onChange={setField('inventoryHybridNote')}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="d-flex flex-wrap justify-content-between align-items-center sp-actions-row px-1 pb-2" style={{ marginTop: 12 }}>
                  <button type="button" className="ile-btn-ghost" onClick={goPrev}><FaChevronLeft size={12} />Previous</button>
                  <div className="d-flex flex-wrap gap-2">
                    <button type="button" className="ile-btn-ghost" onClick={saveDraft} disabled={savingDraft || publishing || mediaUploading}><FaSave className="me-1" />{savingDraft ? 'Saving…' : 'Save as Draft'}</button>
                    <button type="button" className="ile-btn-next" onClick={goNext}>Next<FaChevronRight size={12} /></button>
                  </div>
                </div>
              </TabPane>

              <TabPane
                tab={(
                  <span>
                    {tabIcon('M3 3h4v2H3V3zm0 3h1v1H3V6zm0 2h1v1H3V8zm0 2h1v1H3v-2zM3 2h1v1H2V2zm0 2h1v1H2V4zm0 2h1v1H2V6zm0 2h1v1H2V8zm0 2h1v1H2v-2zM2 2h1v1H1V2zm0 2h1v1H1V4zm0 2h1v1H1V6zm0 2h1v1H1V8zm0 2h1v1H1v-2z')}
                    Media
                  </span>
                )}
                key="5"
              >
                <div className="container-fluid p-0">
                  <div className="sp-subcard">
                    <h6 className="sp-section-title">Media &amp; documents</h6>
                    <p className="ile-mute">Fill in all required fields to create your listing</p>
                    {mediaUploading && (
                      <p className="ile-mute" style={{ marginTop: 4 }}>Uploading to server…</p>
                    )}
                    <div className="mb-3">
                      <span className="ile-form-label">Images / photos *</span>
                      <label className="ile-upload" style={{ pointerEvents: mediaUploading ? 'none' : undefined, opacity: mediaUploading ? 0.6 : 1 }}>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg"
                          multiple
                          disabled={mediaUploading}
                          onChange={onImagesChange}
                        />
                        <div>↑<br />Click to upload or drag and drop</div>
                        <small>
                          PNG, JPG up to 10MB (max 20 images). Files upload immediately.
                          {Array.isArray(f.images) && f.images.length > 0
                            && ` — ${f.images.length} on file`}
                        </small>
                      </label>
                      {Array.isArray(f.images) && f.images.length > 0 && (
                        <ul className="ile-media-list list-unstyled mb-0 mt-2">
                          {f.images.map((x, i) => {
                            const fileHref = mediaEntryHref(x);
                            return (
                              <li key={mediaEntryName(x) + String(i)} className="d-flex align-items-center gap-2 flex-wrap">
                                <span className="text-truncate" style={{ maxWidth: 260 }} title={mediaEntryName(x)}>
                                  {mediaEntryName(x)}
                                </span>
                                {fileHref && (
                                  <a href={fileHref} className="small" target="_blank" rel="noopener noreferrer">View</a>
                                )}
                                <button type="button" className="btn btn-sm btn-link text-danger p-0" onClick={removeImageAt(i)} disabled={mediaUploading}>
                                  Remove
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                    <div className="mb-3">
                      <span className="ile-form-label">Videos (optional)</span>
                      <label className="ile-upload" style={{ pointerEvents: mediaUploading ? 'none' : undefined, opacity: mediaUploading ? 0.6 : 1 }}>
                        <input
                          type="file"
                          accept="video/*"
                          multiple
                          disabled={mediaUploading}
                          onChange={onVideosChange}
                        />
                        <div>↑<br />Upload videos or add YouTube links in description</div>
                        <small>Up to 5 files, 200MB each. Files upload to the server when selected.</small>
                      </label>
                      {Array.isArray(f.videos) && f.videos.length > 0 && (
                        <ul className="ile-media-list list-unstyled mb-0 mt-2">
                          {f.videos.map((x, i) => {
                            const fileHref = mediaEntryHref(x);
                            return (
                              <li key={mediaEntryName(x) + String(i)} className="d-flex align-items-center gap-2 flex-wrap">
                                <span className="text-truncate" style={{ maxWidth: 260 }} title={mediaEntryName(x)}>
                                  {mediaEntryName(x)}
                                </span>
                                {fileHref && (
                                  <a href={fileHref} className="small" target="_blank" rel="noopener noreferrer">View</a>
                                )}
                                <button type="button" className="btn btn-sm btn-link text-danger p-0" onClick={removeVideoAt(i)} disabled={mediaUploading}>
                                  Remove
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                    <h6 className="sp-section-title">Property documents</h6>
                    {[
                      { label: 'Floor plan', formKey: 'floorPlan' },
                      { label: 'Drone view / aerial', formKey: 'drone' },
                      { label: 'Compliance / ownership', formKey: 'complianceDocs' },
                    ].map(({ label, formKey }) => {
                      const fid = `ile-doc-${formKey}`;
                      const v = f[formKey];
                      const docHref = v ? mediaEntryHref(v) : '';
                      return (
                        <div key={formKey} className="ile-doc-row">
                          <span aria-hidden>📎</span>
                          <div className="flex-grow-1" style={{ minWidth: 0 }}>
                            <div>{label}</div>
                            {v && mediaEntryName(v) && (
                              <small className="d-block text-truncate text-success" title={mediaEntryName(v)}>
                                ✓ {mediaEntryName(v)}
                                {docHref && (
                                  <>
                                    {' '}
                                    <a href={docHref} className="small" target="_blank" rel="noopener noreferrer">View</a>
                                  </>
                                )}
                              </small>
                            )}
                            {!v && <small className="text-muted d-block">PDF, PNG, JPG (max 20MB)</small>}
                          </div>
                          <input
                            type="file"
                            className="d-none"
                            id={fid}
                            accept="application/pdf,image/*,.pdf"
                            disabled={mediaUploading}
                            onChange={onPropertyDocumentChange(formKey)}
                          />
                          {v && (
                            <button type="button" className="mb-0 btn btn-sm btn-outline-secondary" onClick={clearPropertyDocument(formKey)} disabled={mediaUploading}>
                              Clear
                            </button>
                          )}
                          <label htmlFor={fid} className="mb-0 btn btn-sm btn-outline-primary" style={{ pointerEvents: mediaUploading ? 'none' : undefined }}>
                            {v ? 'Replace' : 'Upload'}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="d-flex flex-wrap justify-content-between align-items-center sp-actions-row px-1 pb-2" style={{ marginTop: 12 }}>
                  <button type="button" className="ile-btn-ghost" onClick={goPrev}><FaChevronLeft size={12} />Previous</button>
                  <div className="d-flex flex-wrap gap-2">
                    <button type="button" className="ile-btn-ghost" onClick={saveDraft} disabled={savingDraft || publishing || mediaUploading}><FaSave className="me-1" />{savingDraft ? 'Saving…' : 'Save as Draft'}</button>
                    <button type="button" className="ile-btn-next" onClick={goNext}>Next<FaChevronRight size={12} /></button>
                  </div>
                </div>
              </TabPane>

              <TabPane
                tab={(
                  <span>
                    {tabIcon('M20 6L9 17l-5-5')}
                    Review
                  </span>
                )}
                key="6"
              >
                <div className="container-fluid p-0">
                  <div className="sp-subcard">
                    <h6 className="sp-section-title">Additional details &amp; preview</h6>
                    <p className="ile-mute">Fill in all required fields to create your listing</p>
                    <div className="mb-2">
                      <span className="ile-form-label">Detailed description *</span>
                      <textarea
                        className="input-pill"
                        style={{ minHeight: 120 }}
                        placeholder="Provide a detailed description of the property or asset, features, benefits, location, ideal use…"
                        value={f.description}
                        onChange={setField('description')}
                      />
                      <p className="ile-mute" style={{ marginTop: 4 }}>Tip: include unique selling points, nearby landmarks, and business suitability.</p>
                    </div>
                    <div className="mb-3">
                      <span className="ile-form-label">Nearby connectivity &amp; landmarks</span>
                      <textarea
                        className="input-pill"
                        style={{ minHeight: 72 }}
                        placeholder="e.g. 5 km from airport, Near NH-48, Metro 2 km…"
                        value={f.connectivity}
                        onChange={setField('connectivity')}
                      />
                    </div>
                    <h6 className="sp-section-title">Contact preferences</h6>
                    <div className="ps-1">
                      <label className="ile-radio">
                        <input
                          type="radio"
                          name="cp"
                          value="public"
                          checked={f.contactVisibility === 'public'}
                          onChange={() => up({ contactVisibility: 'public' })}
                        />
                        {' '}
                        Public – show contact details to verified users
                      </label>
                      <label className="ile-radio">
                        <input
                          type="radio"
                          name="cp"
                          value="hidden"
                          checked={f.contactVisibility === 'hidden'}
                          onChange={() => up({ contactVisibility: 'hidden' })}
                        />
                        {' '}
                        Hidden – only after enquiry
                      </label>
                      <label className="ile-radio">
                        <input
                          type="radio"
                          name="cp"
                          value="broker"
                          checked={f.contactVisibility === 'broker'}
                          onChange={() => up({ contactVisibility: 'broker' })}
                        />
                        {' '}
                        Broker only
                      </label>
                    </div>
                    <div className="ile-preview-box">
                      <strong>Listing preview</strong>
                      <ul>
                        <li>Title: {f.listingTitle?.trim() || 'Not provided'}</li>
                        <li>Type: {displayListingType}</li>
                        <li>Asset size: {displaySize}</li>
                        {(f.assetSize === 'small' || f.assetSize === 'micro') &&
                          String(f.quantityAvailable || '').trim() && (
                          <li>Quantity available: {f.quantityAvailable}</li>
                        )}
                        <li>Transaction: {String(displayTxn).toLowerCase()}</li>
                        {f.listingType === 'property' && displayPropertyType && (
                          <li>Property type: {displayPropertyType}</li>
                        )}
                        {f.listingType === 'property' && displayFurnishing && <li>Furnishing: {displayFurnishing}</li>}
                        {displayConstruction && <li>Construction: {displayConstruction}</li>}
                        {displayOccupancy && <li>Occupancy: {displayOccupancy}</li>}
                        {displayParkingType && <li>Parking type: {displayParkingType}</li>}
                        {displayLegacy && <li>Legacy: {displayLegacy}</li>}
                        {f.inventoryTrackEnabled && (
                          <>
                            <li>
                              Inventory tracking: on (
                              {(INVENTORY_MODEL_OPTIONS.find((o) => o.value === f.inventoryKind) || {}).label || f.inventoryKind}
                              )
                            </li>
                            {String(f.inventoryTotalUnits || '').trim() && (
                              <li>Total units (inventory): {f.inventoryTotalUnits}</li>
                            )}
                            {String(f.inventoryTotalArea || '').trim() && (
                              <li>Total area (inventory): {f.inventoryTotalArea} {f.inventoryAreaUom || 'sqft'}</li>
                            )}
                            {String(f.inventoryTotalCapacity || '').trim() && (
                              <li>
                                Total capacity: {f.inventoryTotalCapacity}
                                {String(f.inventoryCapacityUom || '').trim() ? ` ${f.inventoryCapacityUom}` : ''}
                              </li>
                            )}
                            <li>Partial leasing allowed: {f.inventoryPartialLeasingAllowed ? 'Yes' : 'No'}</li>
                          </>
                        )}
                      </ul>
                      {f.flexibleTenure && (
                        <div className="ile-preview-ok">✓ Flexible tenure available.</div>
                      )}
                      <div className="mt-2" style={{ fontSize: '0.9rem' }}>
                        <strong>Media on record</strong>
                        <ul className="mb-0" style={{ marginTop: 6 }}>
                          <li>Photos: {Array.isArray(f.images) ? f.images.length : 0}</li>
                          <li>Videos: {Array.isArray(f.videos) ? f.videos.length : 0}</li>
                          <li>
                            Documents: {['floorPlan', 'drone', 'complianceDocs'].filter((k) => f[k] && (mediaEntryHref(f[k]) || (typeof f[k] === 'object' && f[k].name))).length} / 3
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="d-flex flex-wrap justify-content-between align-items-center sp-actions-row px-1 pb-2" style={{ marginTop: 12 }}>
                  <button type="button" className="ile-btn-ghost" onClick={goPrev}><FaChevronLeft size={12} />Previous</button>
                  <div className="d-flex flex-wrap gap-2">
                    <button type="button" className="ile-btn-ghost" onClick={saveDraft} disabled={savingDraft || publishing || mediaUploading}><FaSave className="me-1" />{savingDraft ? 'Saving…' : 'Save as Draft'}</button>
                    <button type="button" className="ile-btn-publish" onClick={publish} disabled={savingDraft || publishing || mediaUploading}>
                      {publishing ? 'Publishing…' : 'Publish listing'}
                    </button>
                  </div>
                </div>
              </TabPane>
            </Tabs>

            <Modal
              open={unsavedOpen}
              title="Leave this page?"
              onCancel={() => setUnsavedOpen(false)}
              footer={[
                <button key="stay" type="button" className="btn btn-outline-primary" onClick={() => setUnsavedOpen(false)}>Stay</button>,
                <button key="go" type="button" className="btn btn-primary" onClick={confirmCancel}>Leave</button>,
              ]}
            >
              <p className="mb-0">You may have unsaved changes. Leave and go to Home?</p>
            </Modal>
          </main>
        </div>
      </div>
    </div>
  );
}
